import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Settings2 } from 'lucide-react';
import type { Profile } from '@yada/types';
import { getProfileIcon, getInitials, getProfileColor } from './profileDisplayUtils';
import styles from './ProfilePopover.module.css';

interface ProfilePopoverProps {
    currentProfile: Profile | null;
    accessibleProfiles: Profile[];
    inaccessibleProfiles: Profile[];
    onSwitch: (profileId: string) => void;
    onManageProfiles: () => void;
    onClose: () => void;
    style?: React.CSSProperties;
}

export const ProfilePopover: React.FC<ProfilePopoverProps> = ({
    currentProfile,
    accessibleProfiles,
    inaccessibleProfiles,
    onSwitch,
    onManageProfiles,
    onClose,
    style,
}) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    const renderProfileRow = (profile: Profile, index: number, accessible: boolean) => {
        const Icon = getProfileIcon(profile.profileType);
        const isActive = profile.id === currentProfile?.id;
        const isHousehold = profile.profileType === 'household';
        const avatar = profile.metadata?.avatar as string | undefined;
        const initials = getInitials(profile.name);
        const color = getProfileColor(profile.id, index);

        return (
            <button
                key={profile.id}
                className={`${styles.profileRow} ${isActive ? styles.profileRowActive : ''} ${!accessible ? styles.profileRowInaccessible : ''}`}
                onClick={() => { onSwitch(profile.id); onClose(); }}
                title={profile.name}
            >
                <div className={styles.avatar} style={!avatar && !isHousehold ? { backgroundColor: color } : undefined}>
                    {avatar ? (
                        <img src={avatar} alt="" className={styles.avatarImg} />
                    ) : isHousehold ? (
                        <Icon size={16} />
                    ) : (
                        <span className={styles.initials}>{initials}</span>
                    )}
                    {!isHousehold && <Icon size={8} className={styles.typeIcon} />}
                </div>
                <span className={styles.profileName}>{profile.name}</span>
                {isActive && <span className={styles.activeDot} aria-hidden="true" />}
            </button>
        );
    };

    const content = (
        <div className={styles.popover} ref={ref} style={style}>
            <div className={styles.list}>
                {accessibleProfiles.map((p, i) => renderProfileRow(p, i, true))}
                {inaccessibleProfiles.length > 0 && (
                    <>
                        <div className={styles.separator} />
                        {inaccessibleProfiles.map((p, i) =>
                            renderProfileRow(p, accessibleProfiles.length + i, false)
                        )}
                    </>
                )}
            </div>
            <div className={styles.footer}>
                <button
                    className={styles.manageButton}
                    onClick={() => { onManageProfiles(); onClose(); }}
                >
                    <Settings2 size={14} />
                    <span>Manage Profiles</span>
                </button>
            </div>
        </div>
    );

    return ReactDOM.createPortal(content, document.body);
};
